import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { 
  FileText, Receipt, Banknote, CheckCircle, XCircle,
  Calculator, TrendingDown, CalendarIcon, Plus, Trash2
} from "lucide-react";
import { format, parse } from "date-fns";
import { ro } from "date-fns/locale";
import { DateAutoInput, parseInputDate, formatDateForDisplay } from "../DateAutoInput";

/**
 * Componenta reutilizabilă pentru gestionarea datelor financiare pe etapă procesuală.
 * Suportă multiple facturi și plăți per etapă.
 * 
 * @param {Object} props
 * @param {Object} props.etapa - Configurația etapei (key, label, color)
 * @param {Object} props.data - Datele financiare ale etapei
 * @param {Function} props.onChange - Callback pentru modificări
 * @param {boolean} props.disabled - Dezactivează editarea
 */
export function EtapaFinanciaraForm({ etapa, data, onChange, disabled }) {
  const handleChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  // Facturi multiple
  const facturi = data.facturi || [];
  
  const addFactura = () => {
    const newFactura = {
      id: `f-${Date.now()}`,
      nr_factura: "",
      data_factura: "",
      valoare: 0
    };
    handleChange("facturi", [...facturi, newFactura]);
  };

  const updateFactura = (index, field, value) => {
    const updated = [...facturi];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("facturi", updated);
  };

  const removeFactura = (index) => {
    const updated = facturi.filter((_, i) => i !== index);
    handleChange("facturi", updated);
  };

  // Chitanțe/Plăți multiple  
  const chitante = data.chitante || [];

  const addChitanta = () => {
    const newChitanta = {
      id: `c-${Date.now()}`,
      nr_chitanta: "",
      data_chitanta: "",
      valoare: 0,
      tip: "numerar" // numerar sau virament
    };
    handleChange("chitante", [...chitante, newChitanta]);
  };

  const updateChitanta = (index, field, value) => {
    const updated = [...chitante];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("chitante", updated);
  };

  const removeChitanta = (index) => {
    const updated = chitante.filter((_, i) => i !== index);
    handleChange("chitante", updated);
  };

  // Calcule totaluri
  const totalFacturat = facturi.reduce((sum, f) => sum + (parseFloat(f.valoare) || 0), 0) + (parseFloat(data.factura_valoare) || 0);
  const totalPlati = chitante.reduce((sum, c) => sum + (parseFloat(c.valoare) || 0), 0) + (parseFloat(data.chitanta_valoare) || 0);
  // Suma neîncasată = Total Facturat - Sumă Încasată Efectiv (introdusă manual)
  const sumaIncasataEfectiv = parseFloat(data.suma_incasata_efectiv) || 0;
  const sumaNeincasata = totalFacturat - sumaIncasataEfectiv;

  return (
    <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
      {/* Contract */}
      <div className="p-3 bg-background rounded border space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileText className="w-4 h-4" />
            Contract de Asistență Juridică
          </div>
          <Badge 
            className={`cursor-pointer transition-colors ${
              data.contract_semnat 
                ? "bg-green-500 hover:bg-green-600 text-white" 
                : "bg-red-500 hover:bg-red-600 text-white"
            }`}
            onClick={() => !disabled && handleChange("contract_semnat", !data.contract_semnat)}
          >
            {data.contract_semnat ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Semnat
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 mr-1" />
                Nesemnat
              </>
            )}
          </Badge>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Nr. Contract</Label>
          <Input
            value={data.contract_nr || ""}
            onChange={(e) => handleChange("contract_nr", e.target.value)}
            placeholder="ex: CAJ-001/2024"
            disabled={disabled}
            className="h-8 text-sm"
          />
        </div>
      </div>

      {/* Factură Fiscală - cu suport pentru multiple */}
      <div className="p-3 bg-background rounded border space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Receipt className="w-4 h-4" />
            Factură Fiscală
          </div>
          {!disabled && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addFactura}
              className="h-7 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Adaugă Factură
            </Button>
          )}
        </div>
        
        {/* Factura principală (legacy) */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Nr. Factură</Label>
            <Input
              value={data.factura_nr || ""}
              onChange={(e) => handleChange("factura_nr", e.target.value)}
              placeholder="ex: F-001"
              disabled={disabled}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data Emiterii</Label>
            <div className="flex gap-1">
              <DateAutoInput
                value={data.factura_data}
                onChange={(val) => handleChange("factura_data", val)}
                disabled={disabled}
                className="h-8 text-sm flex-1"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={disabled}
                  >
                    <CalendarIcon className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={data.factura_data ? parse(data.factura_data, "yyyy-MM-dd", new Date()) : undefined}
                    onSelect={(date) => handleChange("factura_data", date ? format(date, "yyyy-MM-dd") : "")}
                    locale={ro}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valoare (RON)</Label>
            <Input
              type="number"
              value={data.factura_valoare || ""}
              onChange={(e) => handleChange("factura_valoare", parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              disabled={disabled}
              className="h-8 text-sm"
            />
          </div>
        </div>

        {/* Facturi adiționale */}
        {facturi.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            {facturi.map((factura, index) => (
              <div key={factura.id} className="grid grid-cols-3 gap-3 items-end bg-blue-50/50 dark:bg-blue-950/30 p-2 rounded">
                <div className="space-y-1">
                  <Label className="text-xs">Nr. Factură #{index + 2}</Label>
                  <Input
                    value={factura.nr_factura || ""}
                    onChange={(e) => updateFactura(index, "nr_factura", e.target.value)}
                    placeholder="ex: F-002"
                    disabled={disabled}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Emiterii</Label>
                  <div className="flex gap-1">
                    <DateAutoInput
                      value={factura.data_factura}
                      onChange={(val) => updateFactura(index, "data_factura", val)}
                      disabled={disabled}
                      className="h-8 text-sm flex-1"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={disabled}>
                          <CalendarIcon className="h-3 w-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={factura.data_factura ? parse(factura.data_factura, "yyyy-MM-dd", new Date()) : undefined}
                          onSelect={(date) => updateFactura(index, "data_factura", date ? format(date, "yyyy-MM-dd") : "")}
                          locale={ro}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Valoare (RON)</Label>
                    {!disabled && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-red-500 hover:text-red-700"
                        onClick={() => removeFactura(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <Input
                    type="number"
                    value={factura.valoare || ""}
                    onChange={(e) => updateFactura(index, "valoare", parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    disabled={disabled}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total facturat */}
        {(facturi.length > 0 || (data.factura_valoare && data.factura_valoare > 0)) && (
          <div className="pt-2 border-t flex justify-end">
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              Total Facturat: {totalFacturat.toFixed(2)} RON
            </Badge>
          </div>
        )}
      </div>

      {/* Plăți - cu suport pentru multiple */}
      <div className="p-3 bg-background rounded border space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Banknote className="w-4 h-4" />
            Plăți
          </div>
          {!disabled && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addChitanta}
              className="h-7 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Adaugă Plată
            </Button>
          )}
        </div>
        
        {/* Chitanța principală (legacy) */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Nr. Chitanță</Label>
            <Input
              value={data.chitanta_nr || ""}
              onChange={(e) => handleChange("chitanta_nr", e.target.value)}
              placeholder="ex: C-001"
              disabled={disabled}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data Emiterii</Label>
            <div className="flex gap-1">
              <DateAutoInput
                value={data.chitanta_data}
                onChange={(val) => handleChange("chitanta_data", val)}
                disabled={disabled}
                className="h-8 text-sm flex-1"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={disabled}
                  >
                    <CalendarIcon className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={data.chitanta_data ? parse(data.chitanta_data, "yyyy-MM-dd", new Date()) : undefined}
                    onSelect={(date) => handleChange("chitanta_data", date ? format(date, "yyyy-MM-dd") : "")}
                    locale={ro}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valoare (RON)</Label>
            <Input
              type="number"
              value={data.chitanta_valoare || ""}
              onChange={(e) => handleChange("chitanta_valoare", parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              disabled={disabled}
              className="h-8 text-sm"
            />
          </div>
        </div>

        {/* Plăți adiționale - fără dropdown tip */}
        {chitante.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            {chitante.map((chitanta, index) => (
              <div key={chitanta.id} className="grid grid-cols-3 gap-3 items-end bg-green-50/50 dark:bg-green-950/30 p-2 rounded">
                <div className="space-y-1">
                  <Label className="text-xs">Nr. Chitanță #{index + 2}</Label>
                  <Input
                    value={chitanta.nr_chitanta || ""}
                    onChange={(e) => updateChitanta(index, "nr_chitanta", e.target.value)}
                    placeholder="ex: C-002"
                    disabled={disabled}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Emiterii</Label>
                  <div className="flex gap-1">
                    <DateAutoInput
                      value={chitanta.data_chitanta}
                      onChange={(val) => updateChitanta(index, "data_chitanta", val)}
                      disabled={disabled}
                      className="h-8 text-sm flex-1"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={disabled}>
                          <CalendarIcon className="h-3 w-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={chitanta.data_chitanta ? parse(chitanta.data_chitanta, "yyyy-MM-dd", new Date()) : undefined}
                          onSelect={(date) => updateChitanta(index, "data_chitanta", date ? format(date, "yyyy-MM-dd") : "")}
                          locale={ro}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Valoare (RON)</Label>
                    {!disabled && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-red-500 hover:text-red-700"
                        onClick={() => removeChitanta(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <Input
                    type="number"
                    value={chitanta.valoare || ""}
                    onChange={(e) => updateChitanta(index, "valoare", parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    disabled={disabled}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total plăți */}
        {(chitante.length > 0 || (data.chitanta_valoare && data.chitanta_valoare > 0)) && (
          <div className="pt-2 border-t flex justify-end">
            <Badge variant="outline" className="text-green-600 border-green-300">
              Total Plăți: {totalPlati.toFixed(2)} RON
            </Badge>
          </div>
        )}
      </div>

      {/* Sumă Încasată Efectiv */}
      <div className="p-3 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
          <Calculator className="w-4 h-4" />
          Sumă Încasată Efectiv
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-green-600 dark:text-green-400">Sumă Încasată (RON)</Label>
            <Input
              type="number"
              value={data.suma_incasata_efectiv || ""}
              onChange={(e) => handleChange("suma_incasata_efectiv", parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              disabled={disabled}
              className="h-8 text-sm bg-white dark:bg-background"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-amber-600 dark:text-amber-400">Sumă Neîncasată (RON)</Label>
            <div className={`h-8 px-3 py-1 rounded border flex items-center text-sm font-medium ${
              sumaNeincasata > 0 
                ? "bg-amber-100 dark:bg-amber-950 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300"
                : "bg-green-100 dark:bg-green-950 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
            }`}>
              {sumaNeincasata > 0 ? (
                <>
                  <TrendingDown className="w-3 h-3 mr-1" />
                  {sumaNeincasata.toFixed(2)}
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  0.00
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EtapaFinanciaraForm;
